import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Table, Tag, Spin, Select, DatePicker, Button, Space, notification } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import moment from "moment";
import { ApiResponse } from "enum";
import { SolicitudAusenciaService } from "services/VacacionesService";
import { useListarSolicitudesAusenciaPorJefeInmediato } from "../../hooks";
import { SecurityContext } from "context/SecurityContextProvider";

const ListaHistoricoSolicitudes = () => {
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isAceros = getUsuarioAutenticado().empresaSeleccionada.razonSocial.toUpperCase().includes("ACEROS CHILCA SAC")
		? true
		: false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;
	const mostrarFiltros = isSDF || isSDFEnergia;
	// Con filtros (SDF / SDF Energía) la consulta, el filtrado, la paginación y la exportación se hacen en el servidor.
	// El resto de empresas conserva la carga completa de siempre.
	const { solicitudesAusenciaHistorico } = useListarSolicitudesAusenciaPorJefeInmediato(!mostrarFiltros);

	const [filtroTipo, setFiltroTipo] = useState(undefined);
	const [filtroColaborador, setFiltroColaborador] = useState(undefined);
	const [filtroRango, setFiltroRango] = useState(null);
	const [pagina, setPagina] = useState(1);
	const [tamanioPagina, setTamanioPagina] = useState(10);
	const [resultado, setResultado] = useState({ solicitudes: [], total: 0 });
	const [opciones, setOpciones] = useState({ tipos: [], colaboradores: [] });
	const [cargando, setCargando] = useState(false);
	const [exportando, setExportando] = useState(false);
	const ultimaConsulta = useRef(0);

	const parametrosFiltro = useCallback(() => {
		const params = {};
		if (filtroTipo) params.tipoId = filtroTipo;
		if (filtroColaborador) params.colaborador = filtroColaborador;
		if (filtroRango && filtroRango[0] && filtroRango[1]) {
			params.fechaInicioDesde = filtroRango[0].format("YYYY-MM-DD");
			params.fechaInicioHasta = filtroRango[1].format("YYYY-MM-DD");
		}
		return params;
	}, [filtroTipo, filtroColaborador, filtroRango]);

	useEffect(() => {
		if (!mostrarFiltros) {
			return;
		}
		SolicitudAusenciaService.listarOpcionesHistoricoPorJefeInmediato()
			.then((respuesta) => {
				if (respuesta.estado === ApiResponse.EXITO) {
					setOpciones(respuesta.data);
				}
			})
			.catch(() => {});
	}, [mostrarFiltros]);

	useEffect(() => {
		if (!mostrarFiltros) {
			return;
		}
		const consulta = ++ultimaConsulta.current;
		setCargando(true);
		SolicitudAusenciaService.buscarHistoricoPorJefeInmediato({
			...parametrosFiltro(),
			pagina: pagina - 1,
			cantidad: tamanioPagina,
		})
			.then((respuesta) => {
				// Ignora respuestas de consultas anteriores que llegan tarde.
				if (consulta !== ultimaConsulta.current) {
					return;
				}
				if (respuesta.estado === ApiResponse.EXITO) {
					setResultado({ solicitudes: respuesta.data.solicitudes, total: respuesta.data.total });
				} else {
					notification.error({ message: "No se pudo cargar el histórico", description: respuesta.mensaje });
				}
			})
			.catch(() => {
				if (consulta === ultimaConsulta.current) {
					notification.error({ message: "No se pudo cargar el histórico" });
				}
			})
			.finally(() => {
				if (consulta === ultimaConsulta.current) {
					setCargando(false);
				}
			});
	}, [mostrarFiltros, parametrosFiltro, pagina, tamanioPagina]);

	const opcionesTipo = (opciones.tipos || []).map((o) => ({ value: o.id, label: o.descripcion }));
	const opcionesColaborador = (opciones.colaboradores || []).map((o) => ({
		value: o.id,
		label: `${o.descripcion} - ${o.id}`,
	}));

	const solicitudesFiltradas = mostrarFiltros ? resultado.solicitudes : solicitudesAusenciaHistorico || [];

	const hayFiltros = !!filtroTipo || !!filtroColaborador || !!filtroRango;
	const cambiarFiltro = (setter) => (valor) => {
		setter(valor);
		setPagina(1);
	};
	const limpiarFiltros = () => {
		setFiltroTipo(undefined);
		setFiltroColaborador(undefined);
		setFiltroRango(null);
		setPagina(1);
	};

	const exportarExcel = async () => {
		setExportando(true);
		try {
			const respuesta = await SolicitudAusenciaService.exportarHistoricoPorJefeInmediato(parametrosFiltro());
			saveAs(respuesta.data, respuesta.headers.filename || "historico-solicitudes.xlsx");
		} catch (error) {
			notification.error({ message: "No se pudo exportar el histórico" });
		} finally {
			setExportando(false);
		}
	};

	const columnas = [
		{
			title: "Colaborador",
			dataIndex: ["colaborador", "nombreCompleto"],
			width: "20%",
		},
		{
			title:(isSDF || isSDFEnergia) ? "Tipo Movimiento":"Tipo Ausencia",
			dataIndex: ["tipo", "descripcion"],
			width: "15%",
			align: "center",
		},
		{
			title: "Fecha Inicio",
			dataIndex: "fechaInicioAusencia",
			width: "15%",
			align: "center",
			render: (text, fila) => { 
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaInicioAusencia).format(formato);
			}
		},
		{
			title: "Fecha Fin",
			dataIndex: "fechaFin",
			width: "15%",
			align: "center",
			render: (text, fila) => { 
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaFinAusencia).format(formato);
			}
		},
		{
			title: "Dias",
			dataIndex: "diasSolicitados",
			width: "5%",
			align: "center",
		},
		{
			title: "Fecha de Solicitud",
			dataIndex: "fechaHoraSolicitud",
			width: "15%",
			align: "center",
			render: (text, fila) => moment(fila.fechaHoraSolicitud).format("DD/MM/YYYY hh:mm a"),
		},
		{
			title: "Estado",
			//dataIndex: ["jefeInmediato","estadoAprobacion"],
			width: "15%",
			align: "center",
			// render: (text, fila) => (fila.jefeInmediato?.estadoAprobacion === "APROBADO" ? <Tag color="success">{fila.jefeInmediato.estadoAprobacion} </Tag> :
			// 	<Tag color="error">{fila.jefeInmediato?.estadoAprobacion}</Tag>)

			render: (text, fila) => {
				if (mostrarFiltros) {
					// El servidor ya resolvió el estado que corresponde al aprobador que consulta.
					return fila.estadoAprobacion ? (
						<Tag color={fila.estadoAprobacion === "APROBADO" ? "success" : "error"}>{fila.estadoAprobacion}</Tag>
					) : null;
				}
				if (fila.jefeInmediato !== null && fila.jefeInmediato.estadoAprobacion !== "PENDIENTE") {
					if (fila.jefeInmediato?.estadoAprobacion === "APROBADO")
						return <Tag color="success">{fila.jefeInmediato.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.jefeInmediato?.estadoAprobacion}</Tag>;
				} else if (fila.responsableOtrosCargos !== null) {
					if (
						fila.responsableOtrosCargos !== null &&
						fila.responsableOtrosCargos.estadoAprobacion === "APROBADO"
					)
						return <Tag color="success">{fila.responsableOtrosCargos.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.responsableOtrosCargos.estadoAprobacion}</Tag>;
				} else if (fila.responsableAprobacionPrimerNivel !== null
					&& fila.responsableAprobacionPrimerNivel.estadoAprobacion !== "PENDIENTE"
					&& fila.responsableAprobacionPrimerNivel?.numeroDocumentoIdentidad === getUsuarioAutenticado().numeroDocumentoIdentidad) { 
					if (fila.responsableAprobacionPrimerNivel?.estadoAprobacion === "APROBADO")
						return <Tag color="success">{fila.responsableAprobacionPrimerNivel.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.responsableAprobacionPrimerNivel?.estadoAprobacion}</Tag>;
				} else if (fila.responsableOtroAprobacionPrimerNivel !== null
					&& fila.responsableOtroAprobacionPrimerNivel.estadoAprobacion !== "PENDIENTE"
				    && fila.responsableOtroAprobacionPrimerNivel?.numeroDocumentoIdentidad === getUsuarioAutenticado().numeroDocumentoIdentidad) { 
					if (fila.responsableOtroAprobacionPrimerNivel?.estadoAprobacion === "APROBADO")
						return <Tag color="success">{fila.responsableOtroAprobacionPrimerNivel.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.responsableOtroAprobacionPrimerNivel?.estadoAprobacion}</Tag>;
				} else if (fila.responsableAprobacionSegundoNivel !== null
					&& fila.responsableAprobacionSegundoNivel.estadoAprobacion !== "PENDIENTE"
					&& fila.responsableAprobacionSegundoNivel?.numeroDocumentoIdentidad === getUsuarioAutenticado().numeroDocumentoIdentidad
				) { 
					if (fila.responsableAprobacionSegundoNivel?.estadoAprobacion === "APROBADO")
						return <Tag color="success">{fila.responsableAprobacionSegundoNivel.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.responsableAprobacionSegundoNivel?.estadoAprobacion}</Tag>;
				} else if (fila.responsableAprobacionTercerNivel !== null
					&& fila.responsableAprobacionTercerNivel.estadoAprobacion !== "PENDIENTE"
					&& fila.responsableAprobacionTercerNivel?.numeroDocumentoIdentidad === getUsuarioAutenticado().numeroDocumentoIdentidad
				) { 
					if (fila.responsableAprobacionTercerNivel?.estadoAprobacion === "APROBADO")
						return <Tag color="success">{fila.responsableAprobacionTercerNivel.estadoAprobacion} </Tag>;
					else return <Tag color="error">{fila.responsableAprobacionTercerNivel?.estadoAprobacion}</Tag>;
				}
			},
		},
		// {
		// 	title: "Acciones",
		// 	align: "center",
		// 	width: "15%",
		// 	//render: (text, fila) => <MenuAcciones solicitud={fila} />,
		// },
	];
	return (
		<React.Fragment>
		{mostrarFiltros && (
			<Space wrap style={{ marginBottom: 16 }}>
				<Select
					allowClear
					showSearch
					placeholder="Tipo Movimiento"
					style={{ width: 220 }}
					options={opcionesTipo}
					value={filtroTipo}
					onChange={cambiarFiltro(setFiltroTipo)}
					optionFilterProp="label"
				/>
				<Select
					allowClear
					showSearch
					placeholder="Colaborador"
					style={{ width: 300 }}
					options={opcionesColaborador}
					value={filtroColaborador}
					onChange={cambiarFiltro(setFiltroColaborador)}
					optionFilterProp="label"
				/>
				<DatePicker.RangePicker
					format="DD/MM/YYYY"
					placeholder={["Fecha inicio desde", "Fecha inicio hasta"]}
					value={filtroRango}
					onChange={cambiarFiltro(setFiltroRango)}
				/>
				<Button onClick={limpiarFiltros} disabled={!hayFiltros}>
					Limpiar
				</Button>
				<Button type="primary" icon={<DownloadOutlined />} onClick={exportarExcel} loading={exportando} disabled={resultado.total === 0}>
					Exportar Excel
				</Button>
			</Space>
		)}
		<Table
			rowKey={(value) => value.id}
			columns={columnas}
			dataSource={solicitudesFiltradas}
			loading={mostrarFiltros ? cargando : false}
			pagination={
				mostrarFiltros
					? {
							current: pagina,
							pageSize: tamanioPagina,
							total: resultado.total,
							showSizeChanger: true,
							showTotal: (total) => `${total} registros`,
							onChange: (nuevaPagina, nuevoTamanio) => {
								setPagina(nuevoTamanio !== tamanioPagina ? 1 : nuevaPagina);
								setTamanioPagina(nuevoTamanio);
							},
					  }
					: undefined
			}
			expandable={{
				expandedRowRender: (fila) => {
					if (fila.tipo?.descripcion?.toLowerCase() === "cambio de grupo") {
						return <HistorialCambioGrupo registro={fila} />;
					}
					return <Comentario registro={fila} />;
				},
				rowExpandable: (fila) =>
					fila.tipo?.descripcion?.toLowerCase() === "cambio de grupo" ||
					((isMaccaferriConstruction || isMaccaferriDePeru) &&
						fila.comentario !== null &&
						fila.comentario !== undefined &&
						fila.comentario !== "") ||
					(isAceros && fila.informacionAdicional !== null)
					|| (isSDF)
				    || (isSDFEnergia),
			}}
		/>
		</React.Fragment>
	);
};
const Comentario = ({ registro }) => {
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isAceros = getUsuarioAutenticado().empresaSeleccionada.razonSocial.toUpperCase().includes("ACEROS CHILCA SAC")
		? true
		: false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;

	return (
		<React.Fragment>
			{(isMaccaferriDePeru || isMaccaferriConstruction) && (
				<p style={{ margin: 5 }}>{"EMPLEADO DESIGNADO: " + registro.comentario}</p>
			)}
			{isAceros && (
				<div style={{ marginLeft: 50 }}>
					<p>
						<strong>Fecha sobre tiempo: </strong>
						{moment(registro.informacionAdicional.fechaSobreTiempo).format("DD/MM/YYYY")}
					</p>
					<p>
						<strong>Día de compensación: </strong>{" "}
						{moment(registro.informacionAdicional.diaCompensacion).format("DD/MM/YYYY")}
					</p>
					<p>
						<strong>Cantidad de horas: </strong> {registro.informacionAdicional.cantidadHoras}
					</p>
					<p>
						<strong>Horas compensadas: </strong> {registro.informacionAdicional.horasCompensadas}
					</p>
				</div>
			)}	
			{(isSDF || isSDFEnergia) && (
				<>
					{registro.informacionAdicional ? (
						<div style={{ marginLeft: 50 }}>
							{Object.entries(registro.informacionAdicional).map(([key, value], index) => (
								<p key={index}>
									<strong>{key.replace(/([A-Z])/g, " $1").toUpperCase()}: </strong>
									{typeof value === "boolean"
										? value ? "Sí" : "No"
										: Array.isArray(value)
										? value.map((item, i) => (
											<span key={i} style={{ display: "block" }}>
												{typeof item === "object" && item !== null
													? Object.entries(item).map(([k, v]) =>
														`${k}: ${typeof v === "string" && v.match(/^\d{4}-\d{2}-\d{2}T/) && moment(v).isValid() ? moment(v).format("DD/MM/YYYY") : v}`
													).join(" | ")
													: String(item)}
											</span>
										))
										: typeof value === "object" && value !== null
										? JSON.stringify(value)
										: typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/) && moment(value).isValid()
										? moment(value).format("DD/MM/YYYY")
										: value}
								</p>
							))}
							<p>
								<strong>Comentario: </strong> {registro.comentario}
							</p>
						</div>
					) : (
						<p>
							<strong>Comentario: </strong> {registro.comentario}
						</p>
					)}
				</>
			)}
		</React.Fragment>
	);
};

const HistorialCambioGrupo = ({ registro }) => {
	const [historial, setHistorial] = useState([]);
	const [cargando, setCargando] = useState(true);

	useEffect(() => {
		SolicitudAusenciaService.listarHistorialCambioGrupo(registro.colaborador.numeroDocumentoIdentidad)
			.then((data) => {
				const lista = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
				setHistorial(lista);
			})
			.catch(() => setHistorial([]))
			.finally(() => setCargando(false));
	}, [registro.colaborador.numeroDocumentoIdentidad]);

	const fechaActual = registro.informacionAdicional?.cambioGrupo?.[0]?.fecha;

	if (cargando) return <Spin size="small" style={{ marginLeft: 16 }} />;

	const columnas = [
		{
			title: "Fecha",
			dataIndex: "fecha",
			render: (val) => moment(val).format("DD/MM/YYYY"),
		},
		{
			title: "Grupo",
			dataIndex: "grupo",
		},
	];

	const filas = historial.map((item, i) => {
		const esActual = fechaActual && moment(item.fecha).isSame(moment(fechaActual), "day");
		return { ...item, _key: `hist-${i}`, _esActual: esActual };
	});

	return (
		<div style={{ marginLeft: 48 }}>
			<Table
				rowKey="_key"
				columns={columnas}
				dataSource={filas}
				pagination={false}
				size="small"
				style={{ width: 360 }}
				onRow={(fila) => ({
					style: fila._esActual
						? { background: "#e6f7ff", fontWeight: 600 }
						: { background: "#f5f5f5", color: "#888" },
				})}
			/>
			{registro.comentario && (
				<p style={{ margin: "8px 0 0 0" }}>
					<strong>Comentario: </strong>{registro.comentario}
				</p>
			)}
		</div>
	);
};

export default ListaHistoricoSolicitudes;
